import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/api";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartTitle,
  Tooltip,
  Legend
);

const pointValueLabelPlugin = {
  id: "pointValueLabelPlugin",
  afterDatasetsDraw(chart, args, pluginOptions) {
    const { ctx, chartArea } = chart;
    const show = pluginOptions?.show !== false;
    if (!show) return;

    const datasetIndex = pluginOptions?.datasetIndex ?? 0;
    const meta = chart.getDatasetMeta(datasetIndex);
    const data = chart.data?.datasets?.[datasetIndex]?.data ?? [];

    ctx.save();
    ctx.textAlign = "center";
    ctx.font =
      "12px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial";

    meta.data.forEach((point, idx) => {
      const v = data[idx];
      if (v === null || v === undefined || Number.isNaN(Number(v))) return;

      const text = pluginOptions?.formatValue
        ? pluginOptions.formatValue(v)
        : String(v);

      const offsetUp = pluginOptions?.offsetY ?? 10;
      const offsetDown = pluginOptions?.offsetYDown ?? 12;

      let y = point.y - offsetUp;
      let baseline = "bottom";

      const safeTop = (chartArea?.top ?? 0) + 12;
      if (y < safeTop) {
        y = point.y + offsetDown;
        baseline = "top";
      }

      ctx.textBaseline = baseline;

      ctx.lineWidth = 3;
      ctx.strokeStyle = "rgba(255,255,255,0.92)";
      ctx.strokeText(text, point.x, y);

      ctx.fillStyle = "#24152F";
      ctx.fillText(text, point.x, y);
    });

    ctx.restore();
  },
};

const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const formatPtNumber = (n, digits = 1) => {
  if (n === null || n === undefined) return "";
  const num = Number(n);
  if (!Number.isFinite(num)) return "";
  try {
    return new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: digits,
    }).format(num);
  } catch {
    return String(Number(num.toFixed(digits))).replace(".", ",");
  }
};

const formatDateBR = (dateValue) => {
  if (!dateValue) return "";
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return d.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const calcYScale = (series, padPercent = 0.08) => {
  const candidates = (Array.isArray(series) ? series : [])
    .map((p) => safeNumber(p?.value))
    .filter((v) => v != null);

  if (!candidates.length) return null;

  let min = Math.min(...candidates);
  let max = Math.max(...candidates);

  const range = Math.max(1, max - min);
  const pad = Math.max(1, range * padPercent);

  min = Math.floor((min - pad) * 10) / 10;
  max = Math.ceil((max + pad) * 10) / 10;

  if (!(max > min)) {
    min = Math.max(0, min - 2);
    max = max + 2;
  }

  return { min, max };
};

const Evolution = () => {
  const [doses, setDoses] = useState([]);
  const [weightsSummary, setWeightsSummary] = useState(null);
  const [profile, setProfile] = useState(null);

  const [weightLogs, setWeightLogs] = useState([]);
  const [waterLogs, setWaterLogs] = useState([]);
  const [bodyMeasures, setBodyMeasures] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const normalizeReportArray = (val) => (Array.isArray(val) ? val : []);

  const normalizeWeightLogs = (list) => {
    const arr = normalizeReportArray(list);
    return arr
      .map((x) => ({
        date:
          x?.date ??
          x?.logged_at ??
          x?.loggedAt ??
          x?.created_at ??
          x?.createdAt ??
          null,
        weightKg: safeNumber(
          x?.weightKg ?? x?.weight_kg ?? x?.weight ?? x?.value
        ),
      }))
      .filter((x) => x.date && x.weightKg != null)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const normalizeWaterLogs = (list) => {
    const arr = normalizeReportArray(list);
    return arr
      .map((x) => ({
        date:
          x?.date ??
          x?.logged_at ??
          x?.loggedAt ??
          x?.created_at ??
          x?.createdAt ??
          null,
        ml: safeNumber(x?.ml),
        cups: safeNumber(x?.cups),
      }))
      .filter((x) => x.date && (x.ml != null || x.cups != null))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const normalizeBodyMeasures = (list) => {
    const arr = normalizeReportArray(list);
    return arr
      .map((x) => ({
        date:
          x?.date ??
          x?.measured_at ??
          x?.measuredAt ??
          x?.created_at ??
          x?.createdAt ??
          null,
        waistCm: safeNumber(x?.waistCm ?? x?.waist_cm),
        hipCm: safeNumber(x?.hipCm ?? x?.hip_cm),
        chestCm: safeNumber(x?.chestCm ?? x?.chest_cm ?? x?.bust_cm),
        leftArmCm: safeNumber(x?.leftArmCm ?? x?.left_arm_cm),
        rightArmCm: safeNumber(x?.rightArmCm ?? x?.right_arm_cm),
        leftThighCm: safeNumber(x?.leftThighCm ?? x?.left_thigh_cm),
        rightThighCm: safeNumber(x?.rightThighCm ?? x?.right_thigh_cm),
      }))
      .filter((x) => x.date)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  useEffect(() => {
    let cancelled = false;

    const fetchReportData = async () => {
      setIsLoading(true);
      setError("");

      try {
        const [res, profRes] = await Promise.all([
          api.get(`/reports/evolution?view=weekly`),
          api.get(`/users/profile`).catch(() => ({})),
        ]);
        if (cancelled) return;

        const report = res?.data || {};
        const userProf = profRes?.data?.profile || profRes?.data || null;

        setDoses(normalizeReportArray(report.doses));
        setWeightsSummary(report.weights || null);

        const reportWeightLogs =
          report.weightLogs ??
          report.weight_logs ??
          report.weightsLogs ??
          report.weights_logs ??
          [];
        const reportWaterLogs =
          report.waterLogs ?? report.water_logs ?? report.water ?? [];
        const reportBodyMeasures =
          report.bodyMeasurements ??
          report.body_measurements ??
          report.measures ??
          [];

        setWeightLogs(normalizeWeightLogs(reportWeightLogs));
        setWaterLogs(normalizeWaterLogs(reportWaterLogs));
        setBodyMeasures(normalizeBodyMeasures(reportBodyMeasures));

        if (userProf) setProfile(userProf);
        else if (report.profile) setProfile(report.profile);

        setIsLoading(false);
        return;
      } catch (err) {
        if (cancelled) return;
      }

      try {
        const [dosesRes, profileRes, weightRes, bodyRes, waterRes] =
          await Promise.allSettled([
            api.get("/doses"),
            api.get("/users/profile"),
            api.get("/measurements/weight"),
            api.get("/measurements/body"),
            api.get("/water/logs"),
          ]);

        if (dosesRes.status === "fulfilled") {
          setDoses(Array.isArray(dosesRes.value.data) ? dosesRes.value.data : []);
        } else {
          setDoses([]);
        }

        if (profileRes.status === "fulfilled") {
          const p =
            profileRes.value?.data?.profile ?? profileRes.value?.data ?? null;
          setProfile(p || null);
          if (p) {
            setWeightsSummary({
              start: p.startWeight ?? null,
              current: p.currentWeight ?? null,
              goal: p.goalWeight ?? null,
              height: p.height ?? null,
            });
          } else {
            setWeightsSummary(null);
          }
        } else {
          setProfile(null);
          setWeightsSummary(null);
        }

        if (weightRes.status === "fulfilled") {
          const items =
            weightRes.value?.data?.items ?? weightRes.value?.data ?? [];
          setWeightLogs(normalizeWeightLogs(items));
        } else {
          setWeightLogs([]);
        }

        if (bodyRes.status === "fulfilled") {
          const items =
            bodyRes.value?.data?.items ?? bodyRes.value?.data ?? [];
          setBodyMeasures(normalizeBodyMeasures(items));
        } else {
          setBodyMeasures([]);
        }

        if (waterRes.status === "fulfilled") {
          const items =
            waterRes.value?.data?.items ?? waterRes.value?.data ?? [];
          setWaterLogs(normalizeWaterLogs(items));
        } else {
          setWaterLogs([]);
        }
      } catch (fallbackErr) {
        setError("Não foi possível carregar os dados de evolução.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchReportData();

    return () => {
      cancelled = true;
    };
  }, []);

  const appliedDoses = useMemo(() => {
    const list = Array.isArray(doses) ? doses : [];
    return list
      .map((d) => {
        const date = d?.date ?? d?.dose_date ?? d?.doseDate ?? null;
        const dosage =
          d?.dosage ?? d?.dosage_mg ?? d?.dosageMg ?? d?.dosageMG;
        const status = d?.status;
        return { date, dosageMg: safeNumber(dosage), status };
      })
      .filter((d) => d.status === "APLICADA" && d.date && d.dosageMg != null)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [doses]);

  // Série de Peso para o gráfico arrastável
  const weightSeries = useMemo(() => {
    return weightLogs.map((log) => ({
      label: formatDateBR(log.date),
      value: log.weightKg,
      date: log.date,
    }));
  }, [weightLogs]);

  // Datas do Período (Peso Inicial até Hoje)
  const initialWeightDateFormatted = useMemo(() => {
    if (weightLogs.length > 0 && weightLogs[0].date) {
      return formatDateBR(weightLogs[0].date);
    }
    if (profile?.createdAt) {
      return formatDateBR(profile.createdAt);
    }
    return "01/01/2026";
  }, [weightLogs, profile]);

  const todayDateFormatted = useMemo(() => {
    return formatDateBR(new Date());
  }, []);

  // Série de Dosagem Aplicada (Gráfico evolutivo de linha)
  const dosageTimelineSeries = useMemo(() => {
    return appliedDoses.map((d) => ({
      label: formatDateBR(d.date),
      value: d.dosageMg,
      date: d.date,
    }));
  }, [appliedDoses]);

  const heightM = useMemo(() => {
    const h = safeNumber(
      weightsSummary?.height ?? profile?.height ?? profile?.heightCm ?? profile?.height_cm
    );
    if (!h) return null;
    if (h > 3) return h / 100;
    return h;
  }, [weightsSummary, profile]);

  // Série de IMC (Gráfico evolutivo de linha)
  const bmiSeries = useMemo(() => {
    if (!heightM) return [];
    return weightSeries
      .map((p) => {
        const bmi = p.value / (heightM * heightM);
        const n = Number.isFinite(bmi) ? Math.round(bmi * 10) / 10 : null;
        return { label: p.label, value: n, date: p.date };
      })
      .filter((x) => x.value != null);
  }, [heightM, weightSeries]);

  const weightFacts = useMemo(() => {
    const start = safeNumber(weightsSummary?.start ?? profile?.startWeight);
    const goal = safeNumber(weightsSummary?.goal ?? profile?.goalWeight);

    const lastLog = weightLogs.length
      ? safeNumber(weightLogs[weightLogs.length - 1]?.weightKg)
      : null;

    const profileCurrentRaw = safeNumber(
      weightsSummary?.current ?? profile?.currentWeight
    );
    const profileCurrent =
      profileCurrentRaw && profileCurrentRaw > 0 ? profileCurrentRaw : null;

    const current = lastLog ?? profileCurrent ?? null;

    const canCompute = start != null && current != null;
    const diff = canCompute ? Math.round((start - current) * 10) / 10 : null;

    return { start, current, goal, diff, hasLog: lastLog != null };
  }, [weightsSummary, profile, weightLogs]);

  const trendColor = (series) => {
    if (!Array.isArray(series) || series.length < 2) {
      return "#6C54D8";
    }
    const first = Number(series[0]?.value);
    const last = Number(series[series.length - 1]?.value);
    if (!Number.isFinite(first) || !Number.isFinite(last)) {
      return "#6C54D8";
    }
    return last <= first ? "#059669" : "#DC2626";
  };

  const buildWeightChartDataWithGoal = (series, weightColor, goalKg) => {
    const labels = series.map((x) => x.label);
    const weightData = series.map((x) => x.value);

    const datasets = [
      {
        label: "Peso (kg)",
        data: weightData,
        fill: false,
        tension: 0.35,
        pointRadius: 5,
        pointHoverRadius: 7,
        borderWidth: 3,
        borderColor: weightColor,
        backgroundColor: weightColor,
      },
    ];

    const goal = safeNumber(goalKg);
    if (goal != null && labels.length) {
      datasets.push({
        label: "Meta (kg)",
        data: labels.map(() => goal),
        fill: false,
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
        borderWidth: 2,
        borderDash: [6, 6],
        borderColor: "rgba(16,185,129,0.85)",
        backgroundColor: "rgba(16,185,129,0.85)",
      });

      const hitIndex = weightData.findIndex(
        (v) => Number.isFinite(v) && v <= goal
      );

      if (hitIndex >= 0) {
        const hitData = labels.map((_, i) =>
          i === hitIndex ? weightData[i] : null
        );
        datasets.push({
          label: "Meta atingida",
          data: hitData,
          showLine: false,
          pointRadius: labels.map((_, i) => (i === hitIndex ? 8 : 0)),
          pointHoverRadius: labels.map((_, i) => (i === hitIndex ? 10 : 0)),
          borderWidth: 0,
          borderColor: "rgba(16,185,129,0.95)",
          backgroundColor: "rgba(16,185,129,0.95)",
        });
      }
    }

    return { labels, datasets };
  };

  const buildGenericChartData = (series, lineColor, datasetLabel = "Valor") => {
    const labels = series.map((x) => x.label);
    const data = series.map((x) => x.value);

    return {
      labels,
      datasets: [
        {
          label: datasetLabel,
          data,
          fill: false,
          tension: 0.35,
          pointRadius: 5,
          pointHoverRadius: 7,
          borderWidth: 3,
          borderColor: lineColor,
          backgroundColor: lineColor,
        },
      ],
    };
  };

  const chartOptions = (titleText, formatValueFn, extraScales = null) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: Boolean(titleText),
        text: titleText || "",
        font: {
          size: 14,
          family:
            "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial",
          weight: "700",
        },
        color: "#24152F",
        padding: { bottom: 10 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const v = ctx.parsed?.y;
            return formatValueFn ? formatValueFn(v) : String(v);
          },
        },
      },
      pointValueLabelPlugin: {
        show: true,
        formatValue: formatValueFn,
        datasetIndex: 0,
        offsetY: 10,
        offsetYDown: 12,
      },
    },
    scales: {
      x: {
        grid: { color: "rgba(236,231,250,0.6)" },
        ticks: { color: "#6D6680", font: { size: 11, weight: "600" } },
      },
      y: {
        grid: { color: "rgba(236,231,250,0.6)" },
        ticks: { color: "#6D6680", font: { size: 11, weight: "600" } },
        ...(extraScales?.y || {}),
      },
      ...(extraScales || {}),
    },
  });

  const EmptyState = ({ title, subtitle }) => (
    <div className="text-center py-6">
      <p className="text-xs font-bold text-[#6D6680]">{title}</p>
      {subtitle ? (
        <p className="text-xs font-medium text-[#6C54D8] mt-1 leading-relaxed">
          {subtitle}
        </p>
      ) : null}
    </div>
  );

  const progress = useMemo(() => {
    const start = weightFacts.start;
    const current = weightFacts.current;
    const goal = weightFacts.goal;

    if (start == null || current == null || goal == null) return null;

    const total = start - goal;
    if (!Number.isFinite(total) || total <= 0) return null;

    const done = start - current;
    const pct = Math.max(0, Math.min(100, (done / total) * 100));

    return { pct, start, current, goal };
  }, [weightFacts]);

  const weightYScale = useMemo(() => {
    const candidates = [];

    weightSeries.forEach((p) => {
      const v = safeNumber(p?.value);
      if (v != null) candidates.push(v);
    });

    const a = safeNumber(weightFacts.start);
    const b = safeNumber(weightFacts.current);
    const c = safeNumber(weightFacts.goal);
    if (a != null) candidates.push(a);
    if (b != null) candidates.push(b);
    if (c != null) candidates.push(c);

    if (!candidates.length) return null;

    let min = Math.min(...candidates);
    let max = Math.max(...candidates);

    const range = Math.max(1, max - min);
    const pad = Math.max(2, range * 0.06);

    min = Math.floor((min - pad) * 10) / 10;
    max = Math.ceil((max + pad) * 10) / 10;

    if (!(max > min)) {
      min = min - 2;
      max = max + 2;
    }

    return { min, max };
  }, [weightSeries, weightFacts]);

  const dosageYScale = useMemo(() => calcYScale(dosageTimelineSeries), [dosageTimelineSeries]);
  const bmiYScale = useMemo(() => calcYScale(bmiSeries), [bmiSeries]);

  if (isLoading) {
    return (
      <div className="space-y-4 py-2 animate-pulse">
        <div className="h-32 rounded-3xl skeleton-loading w-full" />
        <div className="h-56 rounded-3xl skeleton-loading w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="liti-card text-center p-6 border border-red-200 bg-red-50 text-red-800 font-semibold text-xs shadow-xs">
        {error}
      </div>
    );
  }

  const weightLineColor = trendColor(weightSeries);
  const bmiLineColor = trendColor(bmiSeries);

  return (
    <div className="space-y-4">
      {/* CARD HERO DE PESO & PROGRESSO LITI */}
      <section className="liti-card p-5 shadow-sm border border-[#ECE7FA] space-y-4">
        <div className="flex items-baseline justify-between gap-3 min-w-0">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wider text-[#6C54D8]">
              Kg Eliminados
            </p>
            {weightFacts.diff != null ? (
              <p className="text-3xl sm:text-4xl font-bold text-emerald-600 leading-tight mt-1 font-serif">
                {formatPtNumber(weightFacts.diff, 1)}{" "}
                <span className="text-sm font-semibold text-[#6D6680]">kg</span>
              </p>
            ) : (
              <p className="text-base font-bold text-[#24152F] mt-1 font-serif">
                Vamos começar ✨
              </p>
            )}
          </div>

          {/* PERÍODO PERSONALIZADO SOLICITADO */}
          <div className="text-right whitespace-nowrap">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#6D6680]">
              PERÍODO
            </p>
            <p className="text-xs font-bold text-[#24152F] mt-0.5">
              de {initialWeightDateFormatted} a {todayDateFormatted}
            </p>
          </div>
        </div>

        {weightFacts.diff != null ? (
          <>
            {progress ? (
              <div className="space-y-3 pt-1">
                <div className="h-3 rounded-full bg-[#ECE7FA] overflow-hidden border border-[#ECE7FA]">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${progress.pct}%`,
                      background:
                        "linear-gradient(90deg, #6C54D8, #CBF1DB)",
                    }}
                  />
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-[#ECE7FA]/60 rounded-2xl p-3 text-center border border-[#ECE7FA]">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#6D6680]">
                      Inicial
                    </p>
                    <p className="text-sm font-bold text-[#24152F] mt-0.5 font-serif">
                      {weightFacts.start != null
                        ? `${formatPtNumber(weightFacts.start, 1)} kg`
                        : "—"}
                    </p>
                  </div>

                  <div className="bg-[#ECE7FA]/60 rounded-2xl p-3 text-center border border-[#ECE7FA]">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#6D6680]">
                      Atual
                    </p>
                    <p className="text-sm font-bold text-[#24152F] mt-0.5 font-serif">
                      {weightFacts.current != null
                        ? `${formatPtNumber(weightFacts.current, 1)} kg`
                        : "—"}
                    </p>
                  </div>

                  <div className="bg-[#CBF1DB]/60 rounded-2xl p-3 text-center border border-emerald-200">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-900">
                      Meta
                    </p>
                    <p className="text-sm font-bold text-emerald-800 mt-0.5 font-serif">
                      {weightFacts.goal != null
                        ? `${formatPtNumber(weightFacts.goal, 1)} kg`
                        : "—"}
                    </p>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3.5 text-xs text-amber-900 font-semibold">
            {weightFacts.start != null
              ? `Seu perfil já possui um peso inicial (${formatPtNumber(
                  weightFacts.start,
                  1
                )} kg). Agora basta registrar o peso atual para visualizar seu progresso.`
              : "Para calcular seus kg eliminados, informe o peso inicial e o peso atual."}
          </div>
        )}
      </section>

      {/* 1. GRÁFICO DESTACADO "PROGRESSO DE PESO" (ARRASTÁVEL/SCROLLÁVEL) */}
      <section className="liti-card p-5 shadow-sm border border-[#ECE7FA] space-y-3">
        <div className="flex items-center justify-between gap-3 min-w-0">
          <h2 className="text-lg font-bold text-[#24152F] font-serif">
            Progresso de Peso
          </h2>

          {weightFacts.goal != null ? (
            <div className="text-right whitespace-nowrap">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#6D6680]">
                Meta
              </p>
              <p className="text-xs font-extrabold text-emerald-700">
                {formatPtNumber(weightFacts.goal, 1)} kg
              </p>
            </div>
          ) : null}
        </div>

        {weightSeries.length ? (
          <div className="pt-2 overflow-x-auto scrollbar-thin cursor-grab active:cursor-grabbing select-none">
            <div
              style={{
                minWidth: Math.max(weightSeries.length * 65, 480),
                height: 220,
              }}
            >
              <Line
                plugins={[pointValueLabelPlugin]}
                options={chartOptions(
                  null,
                  (v) => `${formatPtNumber(v, 1)} kg`,
                  {
                    y: {
                      ...(weightYScale
                        ? { min: weightYScale.min, max: weightYScale.max }
                        : {}),
                    },
                  }
                )}
                data={buildWeightChartDataWithGoal(
                  weightSeries,
                  weightLineColor,
                  weightFacts.goal
                )}
              />
            </div>
          </div>
        ) : (
          <EmptyState
            title="Ainda não há registros de peso para montar o gráfico."
            subtitle="Registre seu peso na Home e ele aparecerá aqui automaticamente."
          />
        )}

        {weightSeries.length > 0 && (
          <div className="flex items-center justify-between text-[11px] font-medium text-[#6D6680] pt-1">
            <span>
              {weightFacts.goal != null
                ? "A linha pontilhada indica sua meta. O ponto verde marca quando você atingiu."
                : "Acompanhamento evolutivo das suas pesagens registradas."}
            </span>
            <span className="text-[#6C54D8] font-bold">← Arraste para navegar →</span>
          </div>
        )}
      </section>

      {/* 2. GRÁFICO EVOLUTIVO "LINHA DO TEMPO DA DOSAGEM" (ARRASTÁVEL/SCROLLÁVEL) */}
      <section className="liti-card p-5 shadow-sm border border-[#ECE7FA] space-y-3">
        <div className="flex items-center justify-between gap-3 min-w-0">
          <h2 className="text-lg font-bold text-[#24152F] font-serif">
            Linha do Tempo da Dosagem
          </h2>
          {dosageTimelineSeries.length > 0 && (
            <span className="text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full border border-[#ECE7FA] bg-[#FAFAFC] text-[#6C54D8]">
              Doses aplicadas
            </span>
          )}
        </div>

        {dosageTimelineSeries.length ? (
          <div className="pt-2 overflow-x-auto scrollbar-thin cursor-grab active:cursor-grabbing select-none">
            <div
              style={{
                minWidth: Math.max(dosageTimelineSeries.length * 65, 480),
                height: 220,
              }}
            >
              <Line
                plugins={[pointValueLabelPlugin]}
                options={chartOptions(
                  null,
                  (v) => `${formatPtNumber(v, 1)} mg`,
                  {
                    y: {
                      ...(dosageYScale
                        ? { min: dosageYScale.min, max: dosageYScale.max }
                        : {}),
                    },
                  }
                )}
                data={buildGenericChartData(
                  dosageTimelineSeries,
                  "#6C54D8",
                  "Dosagem (mg)"
                )}
              />
            </div>
          </div>
        ) : (
          <EmptyState
            title="Ainda não encontrei doses aplicadas para mostrar aqui."
            subtitle="Quando você marcar uma dose como APLICADA na rotina, ela aparecerá na sua evolução."
          />
        )}

        {dosageTimelineSeries.length > 0 && (
          <div className="flex items-center justify-between text-[11px] font-medium text-[#6D6680] pt-1">
            <span>Evolução das dosagens (mg) registradas como aplicadas na sua rotina.</span>
            <span className="text-[#6C54D8] font-bold">← Arraste para navegar →</span>
          </div>
        )}
      </section>

      {/* 3. GRÁFICO EVOLUTIVO "LINHA DO TEMPO DO IMC" (ARRASTÁVEL/SCROLLÁVEL) */}
      <section className="liti-card p-5 shadow-sm border border-[#ECE7FA] space-y-3">
        <div className="flex items-center justify-between gap-3 min-w-0">
          <h2 className="text-lg font-bold text-[#24152F] font-serif">
            Linha do Tempo do IMC
          </h2>
          {bmiSeries.length > 0 && (
            <span className="text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full border border-[#ECE7FA] bg-[#FAFAFC] text-[#059669]">
              Índice de Massa Corporal
            </span>
          )}
        </div>

        {!heightM ? (
          <EmptyState
            title="Para calcular o IMC, precisamos da sua altura."
            subtitle="Preencha a altura no seu Perfil para habilitar este indicador."
          />
        ) : bmiSeries.length ? (
          <div className="pt-2 overflow-x-auto scrollbar-thin cursor-grab active:cursor-grabbing select-none">
            <div
              style={{
                minWidth: Math.max(bmiSeries.length * 65, 480),
                height: 220,
              }}
            >
              <Line
                plugins={[pointValueLabelPlugin]}
                options={chartOptions(
                  null,
                  (v) => `${formatPtNumber(v, 1)}`,
                  {
                    y: {
                      ...(bmiYScale
                        ? { min: bmiYScale.min, max: bmiYScale.max }
                        : {}),
                    },
                  }
                )}
                data={buildGenericChartData(
                  bmiSeries,
                  bmiLineColor,
                  "IMC"
                )}
              />
            </div>
          </div>
        ) : (
          <EmptyState
            title="Ainda não encontrei medições de peso suficientes para exibir a linha do tempo do IMC."
            subtitle="Registre seus pesos na Home para acompanhar a evolução do IMC."
          />
        )}

        {bmiSeries.length > 0 && (
          <div className="flex items-center justify-between text-[11px] font-medium text-[#6D6680] pt-1">
            <span>Oscilação do seu IMC calculada com base na sua altura ({Math.round(heightM * 100)} cm) e pesagens.</span>
            <span className="text-[#6C54D8] font-bold">← Arraste para navegar →</span>
          </div>
        )}
      </section>
    </div>
  );
};

export default Evolution;